import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, ClipboardCheck, ChevronDown, ChevronUp, Edit, Trash2, CheckSquare, User, RotateCcw } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  xxLargeTextSize,
  tertiaryText,
  baseTextSize,
  borderPrimary,
  rounded,
} from '../../../styling/stylingHelper';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import {
  fetchChecklistsByRestaurant,
  updateChecklistTasks,
  completeChecklist,
  deleteChecklist,
  Checklist,
} from '../../../services/checklists/checklistsService';
import { getTranslatedText, SupportedLanguage } from '../../../services/recipes/recipesService';
import { fetchTeamMembers, TeamMember } from '../../../services/teamMembers/teamMembersService';
import SaveChecklistModal from './SaveChecklistModal';
import { DeleteConfirmationModal } from '../../common/DeleteConfirmationModal';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { useAuth } from '../../../services/authentication/authContext';
import { Navigate } from 'react-router-dom';
import { canAccessChecklistsPage } from '../../../services/accessManagement/accessManagement';

const ChecklistsPage = () => {
  const { t, i18n } = useTranslation();
  const lang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;
  const { backendUser, accountSettings } = useAuth();

  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);
  const restaurantId = selectedRestaurant?.id;

  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState<Checklist | null>(null);
  const [deletingChecklist, setDeletingChecklist] = useState<Checklist | null>(null);
  const [expandedChecklists, setExpandedChecklists] = useState<Set<string>>(new Set());

  // Check if "All Restaurants" is selected
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  // Check if user has access
  const hasAccess = canAccessChecklistsPage(backendUser, accountSettings);

  // Load checklists and team members
  const loadChecklists = useCallback(async () => {
    if (!restaurantId || isAllRestaurants) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [fetchedChecklists, fetchedTeamMembers] = await Promise.all([
        fetchChecklistsByRestaurant(restaurantId),
        fetchTeamMembers(),
      ]);
      setChecklists(fetchedChecklists);
      setTeamMembers(fetchedTeamMembers);
    } catch (error) {
      console.error('Failed to load checklists:', error);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, isAllRestaurants]);

  // Helper to get assignment display info
  const getAssignmentInfo = (checklist: Checklist): { text: string; isAssignedToMe: boolean } => {
    const assignedTo = checklist.assignedTo || [];

    if (assignedTo.length === 0) {
      return { text: '', isAssignedToMe: false };
    }

    const currentUserId = backendUser?.id;
    const isAssignedToMe = currentUserId ? assignedTo.includes(currentUserId) : false;

    if (isAssignedToMe) {
      return { text: t('checklists.assignedToYou') || 'Assigned to you', isAssignedToMe: true };
    }

    // Get names of assigned users
    const assignedNames = assignedTo
      .map((userId) => {
        const member = teamMembers.find((m) => m.id === userId);
        return member ? (member.name || member.email?.split('@')[0]) : null;
      })
      .filter(Boolean);

    if (assignedNames.length === 0) {
      return { text: t('checklists.assigned') || 'Assigned', isAssignedToMe: false };
    }

    if (assignedNames.length === 1) {
      return { text: `${t('checklists.assignedTo') || 'Assigned to'} ${assignedNames[0]}`, isAssignedToMe: false };
    }

    return { text: `${t('checklists.assignedTo') || 'Assigned to'} ${assignedNames[0]} +${assignedNames.length - 1}`, isAssignedToMe: false };
  };

  useEffect(() => {
    loadChecklists();
  }, [loadChecklists]);

  const handleSaveChecklist = async () => {
    await loadChecklists();
    setIsCreateModalOpen(false);
    setEditingChecklist(null);
  };

  const toggleChecklist = (checklistId: string) => {
    setExpandedChecklists((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(checklistId)) {
        newSet.delete(checklistId);
      } else {
        newSet.add(checklistId);
      }
      return newSet;
    });
  };

  const handleTaskToggle = async (checklistId: string, taskId: string) => {
    const checklist = checklists.find((c) => c.id === checklistId);
    if (!checklist) return;

    // Optimistic update: update UI immediately
    const updatedTasks = checklist.tasks.map((task) => {
      if (task.taskId === taskId) {
        if (task.checked) {
          // Uncheck: remove checkedBy and checkedAt
          return {
            ...task,
            checked: false,
            checkedBy: undefined,
            checkedAt: undefined,
          };
        } else {
          // Check: add checkedBy and checkedAt
          return {
            ...task,
            checked: true,
            checkedBy: backendUser?.id || '',
            checkedAt: new Date().toISOString(),
          };
        }
      }
      return task;
    });

    // Update local state optimistically
    const updatedChecklist = {
      ...checklist,
      tasks: updatedTasks,
    };
    setChecklists((prev) =>
      prev.map((c) => (c.id === checklistId ? updatedChecklist : c))
    );

    try {
      // Persist to backend
      const savedChecklist = await updateChecklistTasks(checklistId, updatedTasks);

      // Compare task states (not exact JSON, since timestamps might differ slightly)
      // Only update if there are meaningful differences (e.g., another user made changes)
      const tasksMatch = savedChecklist.tasks.length === updatedTasks.length &&
        savedChecklist.tasks.every((savedTask, index) => {
          const optimisticTask = updatedTasks[index];
          return savedTask.taskId === optimisticTask.taskId &&
            savedTask.checked === optimisticTask.checked &&
            savedTask.checkedBy === optimisticTask.checkedBy &&
            savedTask.title === optimisticTask.title;
        });

      if (!tasksMatch) {
        // There were changes from other users or task structure changed, update with server data
        setChecklists((prev) =>
          prev.map((c) => (c.id === checklistId ? savedChecklist : c))
        );
      }
      // If tasks match, keep the optimistic update (no rerender needed)
    } catch (error) {
      console.error('Failed to update task:', error);
      // Revert optimistic update on error
      setChecklists((prev) =>
        prev.map((c) => (c.id === checklistId ? checklist : c))
      );
    }
  };

  const handleCompleteChecklist = async (checklistId: string) => {
    const checklist = checklists.find((c) => c.id === checklistId);
    if (!checklist) return;

    // Optimistic update: reset all tasks immediately
    const resetTasks = checklist.tasks.map((task) => ({
      ...task,
      checked: false,
      checkedBy: undefined,
      checkedAt: undefined,
    }));

    const updatedChecklist = {
      ...checklist,
      tasks: resetTasks,
    };

    // Update local state optimistically
    setChecklists((prev) =>
      prev.map((c) => (c.id === checklistId ? updatedChecklist : c))
    );

    try {
      // Persist to backend
      const savedChecklist = await completeChecklist(checklistId);

      // Compare task states (not exact JSON, since timestamps might differ slightly)
      // Only update if there are meaningful differences (e.g., another user made changes)
      const tasksMatch = savedChecklist.tasks.length === resetTasks.length &&
        savedChecklist.tasks.every((savedTask, index) => {
          const optimisticTask = resetTasks[index];
          return savedTask.taskId === optimisticTask.taskId &&
            savedTask.checked === optimisticTask.checked &&
            savedTask.checkedBy === optimisticTask.checkedBy &&
            savedTask.title === optimisticTask.title;
        });

      if (!tasksMatch) {
        // There were changes from other users or task structure changed, update with server data
        setChecklists((prev) =>
          prev.map((c) => (c.id === checklistId ? savedChecklist : c))
        );
      }
      // If tasks match, keep the optimistic update (no rerender needed)
    } catch (error) {
      console.error('Failed to complete checklist:', error);
      // Revert optimistic update on error
      setChecklists((prev) =>
        prev.map((c) => (c.id === checklistId ? checklist : c))
      );
    }
  };

  const handleDeleteChecklist = async () => {
    if (!deletingChecklist) return;

    try {
      await deleteChecklist(deletingChecklist.id);
      await loadChecklists();
      setDeletingChecklist(null);
    } catch (error) {
      console.error('Failed to delete checklist:', error);
    }
  };


  if (!hasAccess) {
    return <Navigate to="/" />;
  }

  if (isAllRestaurants) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <ClipboardCheck className="w-8 h-8 text-green-600 dark:text-green-400" />
            <h1 className="text-3xl font-bold text-text dark:text-text-dark">{t('checklists.title')}</h1>
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">
            {t('checklists.pleaseSelectSpecificRestaurant')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <MobilePageHeader title={t('checklists.title')} right={<RestaurantSelector />} />
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'hidden lg:block')}>{t('checklists.title')}</h1>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className={cn(
              smallTextSize,
              'text-white dark:text-white',
              semiBoldText,
              'bg-darkGreenColor dark:bg-darkGreenColor-dark',
              'rounded-xl',
              'px-3 py-2',
              'hover:bg-darkGreenColor/95 dark:hover:bg-darkGreenColor-dark/95',
              'flex items-center gap-2',
              'min-w-[120px]'
            )}
          >
            <Plus className="w-5 h-5" />
            <span className="inline">{t('checklists.addChecklist')}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className={cn(baseTextSize, tertiaryText, 'text-center py-10')}>
          <p>{t('checklists.loading')}</p>
        </div>
      ) : checklists.length === 0 ? (
        <div className={cn(baseTextSize, tertiaryText, 'text-center py-10')}>
          <p className={cn(secondaryText)}>{t('checklists.noChecklists')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {checklists
            .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
            .map((checklist) => {
            const isExpanded = expandedChecklists.has(checklist.id);
            const checkedCount = checklist.tasks.filter((t) => t.checked).length;
            const totalCount = checklist.tasks.length;
            const assignmentInfo = getAssignmentInfo(checklist);

            return (
              <div
                key={checklist.id}
                className={cn(
                  'border',
                  borderPrimary,
                  rounded,
                  'bg-card dark:bg-card-dark',
                  'transition-all'
                )}
              >
                <div className="p-4">
                  <div className="flex max-lg:flex-wrap items-center gap-3">
                    {/* Checklist header */}
                    <div
                      className="flex-1 flex items-center justify-between cursor-pointer"
                      onClick={() => toggleChecklist(checklist.id)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className={cn(baseTextSize, semiBoldText, primaryText)}>
                            {getTranslatedText(checklist.nameTranslations, lang)}
                          </h3>
                          {checklist.clearsDaily && (
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                                'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                              )}
                            >
                              <RotateCcw className="w-3 h-3" />
                              {t('checklists.dailyReset') || 'Daily reset'}
                            </span>
                          )}
                          {assignmentInfo.text && (
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                                assignmentInfo.isAssignedToMe
                                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                  : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                              )}
                            >
                              <User className="w-3 h-3" />
                              {assignmentInfo.text}
                            </span>
                          )}
                        </div>
                        <p className={cn(smallTextSize, secondaryText)}>
                          {checkedCount} / {totalCount} {t('checklists.tasks')}
                        </p>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className={cn('w-5 h-5', secondaryText)} />
                      ) : (
                        <ChevronDown className={cn('w-5 h-5', secondaryText)} />
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCompleteChecklist(checklist.id)}
                        className={cn(
                          smallTextSize,
                          'px-3 py-1.5 rounded-lg',
                          'border',
                          borderPrimary,
                          secondaryText,
                          'hover:bg-gray-50 dark:hover:bg-gray-800',
                          'flex items-center gap-1.5'
                        )}
                        title={t('checklists.completeChecklist')}
                      >
                        <CheckSquare className="w-4 h-4" />
                        <span>{t('checklists.complete')}</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingChecklist(checklist);
                        }}
                        className={cn(
                          'p-2 rounded-lg',
                          'border',
                          borderPrimary,
                          'hover:bg-gray-50 dark:hover:bg-gray-800',
                          'text-gray-600 dark:text-gray-400'
                        )}
                        title={t('checklists.editChecklist')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingChecklist(checklist);
                        }}
                        className={cn(
                          'p-2 rounded-lg',
                          'border',
                          borderPrimary,
                          'hover:bg-red-50 dark:hover:bg-red-900/20',
                          'text-red-600 dark:text-red-400'
                        )}
                        title={t('checklists.deleteChecklist')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded tasks */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
                      {checklist.tasks.length === 0 ? (
                        <p className={cn(smallTextSize, secondaryText)}>{t('checklists.noTasks')}</p>
                      ) : (
                        <div className="space-y-2">
                          {checklist.tasks
                            .sort((a, b) => a.pos - b.pos)
                            .map((task) => (
                              <div
                                key={task.taskId}
                                className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                              >
                                <input
                                  type="checkbox"
                                  checked={task.checked}
                                  onChange={() => handleTaskToggle(checklist.id, task.taskId)}
                                  className="mt-1 w-4 h-4 rounded border-gray-300 dark:border-borderPrimary-dark text-green-600 dark:text-textGreen-dark focus:ring-green-500 dark:focus:ring-green-500"
                                />
                                <div className="flex-1">
                                  <p
                                    className={cn(
                                      smallTextSize,
                                      task.checked ? 'line-through text-gray-400 dark:text-textQuaternary-dark' : primaryText
                                    )}
                                  >
                                    {getTranslatedText(task.titleTranslations, lang) || task.title}
                                  </p>
                                  {task.checked && task.checkedAt && (
                                    <p className={cn(smallTextSize, secondaryText, 'mt-1')}>
                                      {t('checklists.checkedAt')} {new Date(task.checkedAt).toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                      })}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {restaurantId && (
        <SaveChecklistModal
          isOpen={isCreateModalOpen || editingChecklist !== null}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingChecklist(null);
          }}
          mode={editingChecklist ? 'edit' : 'create'}
          restaurantId={restaurantId}
          checklist={editingChecklist || undefined}
          onSave={handleSaveChecklist}
          teamMembers={teamMembers}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deletingChecklist !== null}
        title={t('checklists.confirmDelete')}
        message={t('checklists.confirmDeleteMessage', { name: deletingChecklist ? getTranslatedText(deletingChecklist.nameTranslations, lang) : '' })}
        onConfirm={handleDeleteChecklist}
        onCancel={() => setDeletingChecklist(null)}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
      />
    </div>
  );
};

export default ChecklistsPage;

